import { supabase, STORAGE_BUCKETS } from './supabase';

export interface NFTMetadata {
  name: string;
  description: string;
  image: string;
  external_url: string;
  attributes: Array<{
    trait_type: string;
    value: string;
    display_type?: string;
  }>;
}

export interface TokenData {
  symbol: string;
  logo_url: string | null;
  description: string | null;
  deployer_address: string;
  created_at: string;
  pool_address: string | null;
  nft_token_id: string | null;
  zns_domain?: string | null;  // Optional ZNS domain name
}

/**
 * Generate NFT metadata JSON for a token card
 */
export function generateNFTMetadata(data: TokenData): NFTMetadata {
  const tokenId = data.nft_token_id || 'unknown';
  
  // Format creation date
  const createdDate = data.created_at ? new Date(data.created_at) : new Date();
  const formattedDate = createdDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  // Truncate addresses for display
  const truncateAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Build attributes array
  const attributes: Array<{
    trait_type: string;
    value: string;
    display_type?: string;
  }> = [
    {
      trait_type: "Creator",
      value: data.zns_domain || data.deployer_address  // Use ZNS name if available
    },
    {
      trait_type: "Symbol",
      value: data.symbol
    },
    {
      trait_type: "Pair Address",
      value: data.pool_address || 'N/A'
    },
    {
      trait_type: "NFT ID",
      value: data.nft_token_id || 'N/A'
    },
    {
      trait_type: "Platform",
      value: "Mavrk.ink"
    },
    {
      trait_type: "Created",
      display_type: "date",
      value: Math.floor(createdDate.getTime() / 1000).toString()
    }
  ];

  // Add ZNS-specific attributes if domain exists
  if (data.zns_domain) {
    attributes.push({
      trait_type: "ZNS Domain",
      value: data.zns_domain
    });
    attributes.push({
      trait_type: "ZNS Verified",
      value: "Yes"
    });
    // Also add the wallet address for reference
    attributes.push({
      trait_type: "Creator Address",
      value: data.deployer_address
    });
  }

  // Get contract address from pool_address or use a generic link
  // Note: We'd need to pass contract_address separately if needed for external_url
  const externalUrl = data.pool_address 
    ? `https://mavrk.ink/swap`
    : `https://mavrk.ink/explore`;

  return {
    name: `$${data.symbol} Creator Card`,
    description: data.zns_domain 
      ? `Creator card for ${data.symbol} token launched on Mavrk.ink by ${data.zns_domain}. Created at ${formattedDate}.`
      : `Creator card for ${data.symbol} token launched on Mavrk.ink. Created at ${formattedDate}.`,
    image: data.logo_url || '',
    external_url: externalUrl,
    attributes
  };
}

/**
 * Upload NFT metadata JSON to Supabase
 */
export async function uploadNFTMetadata(
  tokenId: string,
  metadata: NFTMetadata
): Promise<string | null> {
  try {
    console.log(`📤 Uploading NFT metadata for token ID: ${tokenId}`);
    
    const metadataJSON = JSON.stringify(metadata, null, 2);
    const blob = new Blob([metadataJSON], { type: 'application/json' });
    const fileName = `${tokenId}.json`;

    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.CARDS)
      .upload(fileName, blob, {
        contentType: 'application/json',
        upsert: true
      });

    if (error) {
      console.error('❌ Supabase upload error:', error);
      return null;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKETS.CARDS)
      .getPublicUrl(fileName);

    console.log(`✅ Metadata uploaded: ${publicUrl}`);
    return publicUrl;
  } catch (error) {
    console.error('Error uploading NFT metadata:', error);
    return null;
  }
}

/**
 * Generate and upload NFT metadata for a token card
 */
export async function generateAndUploadNFTMetadata(
  tokenAddress: string,
  tokenData: TokenData
): Promise<string | null> {
  try {
    // Derive token ID from token address (matching contract logic)
    const tokenId = BigInt(tokenAddress).toString();

    console.log(`🎨 Generating NFT metadata for token: ${tokenAddress}`);
    
    // Generate metadata
    const metadata = generateNFTMetadata(tokenData);

    // Update image URL to point to the card PNG
    const cardImageUrl = `https://esjrycmiokijtxnbfyox.supabase.co/storage/v1/object/public/token-cards/${tokenAddress}-card.png`;
    metadata.image = cardImageUrl;

    // Upload metadata JSON
    const metadataUrl = await uploadNFTMetadata(tokenId, metadata);

    if (metadataUrl) {
      console.log(`✅ NFT metadata uploaded: ${metadataUrl}`);
      return metadataUrl;
    } else {
      console.error('Failed to upload NFT metadata');
      return null;
    }
  } catch (error) {
    console.error('Error generating NFT metadata:', error);
    return null;
  }
}

